import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/light';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import { githubGist } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { breakpoints } from '@/layouts/breakpoints'
import { Copy, Check } from 'lucide-react'


SyntaxHighlighter.registerLanguage('json', json);

// ─── Types ────────────────────────────────────────────────────────────────────

export type DataSourceTab = {
  title: string;
  description: string;
  data: Record<string, unknown> | unknown[];
};

export type DataSourceViewerProps = {
  heading?: string;
  subheading?: string;
  ticker?: string;
  defaultTab?: string;
  tabs?: DataSourceTab[];
};

// ─── Styled Components ────────────────────────────────────────────────────────

const Wrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 48px 60px;
  color: #111;
  border-top: 1px solid rgba(var(--greyr), 0.3);
  border-bottom: 1px solid rgba(var(--greyr), 0.3);
  @media (max-width: ${breakpoints.medium}) {
    padding: 40px 20px 60px;
  }
`;

const Header = styled.div`
  margin-bottom: 32px;

  h2 {
    font-size: 32px;
    font-weight: 700;
    letter-spacing: -0.3px;
    color: #0d0d0d;
    margin: 0 0 6px;
  }

  p {
    font-size: 14px;
    color: #666;
    margin: 0;
    font-weight: 400;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 40vw 1fr;
  gap: 0;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  @media (max-width: ${breakpoints.medium}) {
    grid-template-columns: 100%;

  }
`;

const Sidebar = styled.nav`
  background: #fafafa;
  border-right: 1px solid #e4e4e0;
`;

const TabButton = styled.button`
  width: 100%;
  text-align: left;
  padding: 16px 20px;
  border: none;
  border-bottom: 1px solid #eee;
  background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  cursor: pointer;
  position: relative;
  transition: background 0.12s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${({ $active }) => ($active ? '#1a73e8' : 'transparent')};
    border-radius: 0 2px 2px 0;
    transition: background 0.12s ease;
  }

  &:hover {
    background: ${({ $active }) => ($active ? '#fff' : '#f0f0ec')};
  }

  .tab-title {
    display: block;
    font-size: 13.5px;
    font-weight: ${({ $active }) => ($active ? '600' : '500')};
    color: ${({ $active }) => ($active ? '#0d0d0d' : '#444')};
    margin-bottom: 3px;
    letter-spacing: -0.1px;
  }

  .tab-desc {
    display: block;
    font-size: 11.5px;
    color: #888;
    font-weight: 400;
    line-height: 1.4;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-height: 500px;
  background: #fff;
  overflow: scroll;

  @media (max-width: ${breakpoints.medium}) {
    min-height: 0px;
    height: auto!important;

  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid #e8e8e4;
  background: #fafafa;
  gap: 12px;
`;

const PanelMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;

  .label {
    color: #999;
    font-weight: 500;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    font-size: 10.5px;
  }

  .status {
    background: #e8f5e9;
    color: #2e7d32;
    font-weight: 600;
    font-size: 11px;
    padding: 2px 7px;
    border-radius: 3px;
  }

  .dot { color: #ccc; }

  .ticker {
    font-size: 12px;
    color: #555;
    font-weight: 500;
  }
`;

const CopyButton = styled.button`
  background: none;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  padding: 4px 8px;
  color: #888;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.12s;

  &:hover {
    background: #f0f0ec;
    color: #444;
    border-color: #ccc;
  }
`;

const CodeArea = styled.div`
  flex: 1;
  overflow: auto;
  padding: 20px 0;

  & pre {
    margin: 0 !important;
    background: #fff !important;
    font-family: 'IBM Plex Mono', 'Fira Code', monospace !important;
    font-size: 12.5px !important;
    line-height: 1.7 !important;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const DataSourceViewer = ({
  heading    = '',
  subheading = '',
  ticker     = '',
  defaultTab,
  tabs       = [],
}: DataSourceViewerProps) => {
  const [activeTab, setActiveTab] = useState(defaultTab ?? tabs[0]?.title ?? '');
  const [copied, setCopied] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [panelMaxHeight, setPanelMaxHeight] = useState<number>();

  tabRefs.current = tabRefs.current.slice(0, tabs.length);

  useEffect(() => {
    const buttons = tabRefs.current.filter((el): el is HTMLButtonElement => el !== null);
    if (buttons.length === 0) return;

    const computeHeight = () => {
      const total = buttons.reduce((sum, el) => sum + el.offsetHeight, 0);
      setPanelMaxHeight(total);
    };

    computeHeight();

    const ro = new ResizeObserver(computeHeight);
    buttons.forEach((el) => ro.observe(el));
    return () => ro.disconnect();
  }, [tabs]);

  const activeData = tabs.find(t => t.title === activeTab)?.data ?? {};
  const jsonString = JSON.stringify(activeData, null, 2);

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Wrapper>
      <Header>
        <h2 className="serif">{heading}</h2>
        <p>{subheading}</p>
      </Header>

      <Layout>
        <Sidebar>
          {tabs.map((tab, index) => (
            <TabButton
              key={tab.title}
              ref={(el) => { tabRefs.current[index] = el; }}
              $active={activeTab === tab.title}
              onClick={() => setActiveTab(tab.title)}
            >
              <span className="tab-title">{tab.title}</span>
              <span className="tab-desc">{tab.description}</span>
            </TabButton>
          ))}
        </Sidebar>

        <Panel style={{ maxHeight: panelMaxHeight }}>
          <PanelHeader>
            <PanelMeta>
              <span className="label">Response</span>
              <span className="status">200 OK</span>
              <span className="dot">•</span>
              <span className="ticker">{ticker}</span>
            </PanelMeta>
            <CopyButton onClick={handleCopy}>
              {copied ? (
                <>
                  <Check size={12} />
                  Copied
                </>
              ) : (
                  <>
                    <Copy size={12} />
                    Copy
                  </>
                )}
            </CopyButton>
          </PanelHeader>

          <CodeArea>
            <SyntaxHighlighter
              language="json"
              style={githubGist}
              wrapLongLines
              customStyle={{
                background: '#fff',
                padding: '0 24px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowX: 'hidden',
              }}
            >
              {jsonString}
            </SyntaxHighlighter>
          </CodeArea>
        </Panel>
      </Layout>
    </Wrapper>
  );
};

export default DataSourceViewer;
